// Imports for external libraries go here.
import React, { FC } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { CustomHeadProps, HeroImage } from './CustomHead.types';
import { PreloadFontsLink } from '@marriott/mi-headless-utils';
import { isCNLocale, buildMediaQuery, getHrefFromSrcSetOrUrl } from '@marriott/shared/mi-helper-utils';

export const extractHeroImageFromModel = (model: CustomHeadProps['pageProps']): HeroImage | null => {
  try {
    const gridItems = model?.model?.cqItems?.root?.[':items']?.responsivegrid?.[':items'];
    if (!gridItems) return null;

    const heroKey = Object.keys(gridItems).find(key => gridItems[key]?.[':type']?.includes('merchandisingherobanner'));

    if (!heroKey) return null;

    const heroComponent = gridItems[heroKey];
    const image = heroComponent?.image;

    if (!image?.assetPath) return null;

    return {
      assetPath: image.assetPath,
      altText: image.altText,
      renditions: image.renditions,
    };
  } catch {
    return null;
  }
};

export const HeroImagePreload: FC<{ heroImage: HeroImage | null }> = ({ heroImage }) => {
  if (!heroImage?.renditions?.length) {
    if (!heroImage?.assetPath) return null;
    return <link rel="preload" as="image" href={heroImage.assetPath} fetchPriority="high" />;
  }

  return (
    <>
      {heroImage.renditions.map((rendition, index) => {
        if (!rendition?.renditionPath || !rendition?.mediaQuery || !rendition?.mediaValue) return null;
        const href = getHrefFromSrcSetOrUrl(rendition.renditionPath);
        const media = buildMediaQuery(rendition.mediaQuery, rendition.mediaValue);
        return (
          <link
            key={`hero-preload-${index}`}
            rel="preload"
            as="image"
            media={media}
            href={href}
            imageSrcSet={rendition.renditionPath}
            fetchPriority="high"
          />
        );
      })}
    </>
  );
};

// below code to block header few seconds to getUserdetails response
export const getHeaderScript = function () {
  return `(function (g, b, d, f) {
    (function (a, c, d) {
        if (a) {
        const e = b.createElement('style');
        e.id = c;
        e.innerHTML = d;
        a.appendChild(e);
        }
    })(b.getElementsByTagName('head')[0], 'at-body-style', d);
    setTimeout(function () {
        const a = b.getElementsByTagName('head')[0];
        if (a) {
        const c = b.getElementById('at-body-style');
        c && a.removeChild(c);
        }
    }, f);
    })(window, document, '.header,.m-header,.ham-header {opacity: 0 !important}', 3E3);`;
};

export const getSessionDataScript = function (sessionData: CustomHeadProps['sessionData']) {
  return `var sessionData = ${sessionData ? JSON.stringify(sessionData) : '{}'};`;
};

// Use named rather than default exports.
export const CustomHead: FC<CustomHeadProps> = ({ sessionData, pageProps, pageLocale }) => {
  const { CLIENT_DATA_LAYER_PATH, NEXT_PUBLIC_ENABLE_WEBSDK } = process.env;
  const { ENABLE_CLIENT_DATA_LAYER } = process.env;

  const dataLayerPath = pageLocale
    ? `${CLIENT_DATA_LAYER_PATH}?locale=${pageLocale}&enableWebSDK=${pageProps?.model?.enableWebSDK ?? 'false'}`
    : `${CLIENT_DATA_LAYER_PATH}?enableWebSDK=${pageProps?.model?.enableWebSDK ?? 'false'}`;

  const heroImage = extractHeroImageFromModel(pageProps);

  return (
    <>
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      {/* CNWEB-2400 The following CSP directive is to avoid a mixed content blocked error for an http asset called from baidu script, and upgrade the request to use https */}
      <meta httpEquiv="Content-Security-Policy" content="upgrade-insecure-requests" />
      <link rel="preconnect" href="https://assets.adobedtm.com" />
      <link rel="dns-prefetch" href="https://assets.adobedtm.com" />
      <link rel="preconnect" href="https://cache.marriott.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://cache.marriott.com" />
      <link rel="dns-prefetch" href="https://cache.marriott.com" />

      {isCNLocale(pageLocale) ? (
        <>
          <link rel="preconnect" href="https://cache.marriott.com.cn" crossOrigin="anonymous" />
          <link rel="preconnect" href="https://cache.marriott.com.cn" />
          <link rel="dns-prefetch" href="https://cache.marriott.com.cn" />
        </>
      ) : null}

      {/* Preload hero image for LCP optimization - must come before fonts */}
      <HeroImagePreload heroImage={heroImage} />

      <PreloadFontsLink locale={pageLocale} />
      <link rel="shortcut icon" href="https://cache.marriott.com/Images/Mobile/MC_Logos/MarriottApple57x57.png" />
      {ENABLE_CLIENT_DATA_LAYER === 'true' && (
        <script type="text/javascript" data-testid="testDataLayer" src={dataLayerPath} defer></script>
      )}
      <script
        id="miSessionData"
        data-testid="miSessionData"
        dangerouslySetInnerHTML={{
          __html: getSessionDataScript(sessionData),
        }}
      ></script>
      {pageProps?.serverEnv && (
        <script
          id="__SERVERENV__"
          type="application/json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(pageProps.serverEnv) }}
        />
      )}

      {NEXT_PUBLIC_ENABLE_WEBSDK === 'true' && pageProps?.model?.enableWebSDK === 'true' ? (
        <script type="text/javascript" id="launch_script" src={pageProps?.model?.webSdkLaunchScript}></script>
      ) : (
        <script type="text/javascript" id="launch_script" src={pageProps?.model?.dataLayerLaunchScript} defer></script>
      )}

      <script
        id="miHeaderVisible"
        dangerouslySetInnerHTML={{
          __html: getHeaderScript(),
        }}
      ></script>
    </>
  );
};
